import { chain, uniq, values } from 'lodash';
import {
  afterSubtypePostfixes,
  friendlySampleType,
  SampleSubType,
  SampleType,
  SmplPntSubTypePostFix,
} from './sampling-point-types';
import { FullSample, MetricMode, ProcessMode, Sample, SamplingPoint } from './types';

export interface SmplPntGroup {
  type: SampleTypeGroup;
  smplPnts: SamplingPoint[];
}

export enum AllProjectSampleTypesGroup {
  ALL_PROJECT_SAMPLING_POINTS = 'ALL_PROJECT_SAMPLING_POINTS',
}

export type SampleTypeGroup = ComparableSampleGroupType | SampleType | AllProjectSampleTypesGroup;

// Biologically comparable sampling point groups of sampling points that can be shown in overview at the same time
// NOTE: groups are not mutually exclusive
// NOTE: ALL_PROJECT_SAMPLING_POINTS may be uncomparable, but it's included for backwards compatibility
interface ComparableSampleGroupSpec {
  type: SampleTypeGroup;
  processMode: ProcessMode | undefined;
  smplPntFilterer: (smplPnt: Pick<SamplingPoint, 'type' | 'subtype'>) => boolean;
}

// This is a "SampleSubtypeGroup" of biologically comparable smplPnt subtypes
export enum ComparableSampleGroupType {
  COMMUNAL_WASTEWATER = 'COMMUNAL_WASTEWATER',
  CLINICAL_WASTEWATER = 'CLINICAL_WASTEWATER',
  RAW_WASTEWATER = 'RAW_WASTEWATER',
  TREATED_WASTEWATER = 'TREATED_WASTEWATER', // Consider deprecating in favor of just WATER_TREATMENT, (see HACK in OverviewContextProvider)
  WATER_TREATMENT = 'WATER_TREATMENT', // Treatment "processes"
  // WATER_PROCESSES = 'WATER_PROCESSES', // All water processes
}
export const allSampleGroupTypes = [
  ...values(ComparableSampleGroupType), // See above
  ...values(SampleType), // Should work as is
  ...values(AllProjectSampleTypesGroup), // Already excluded
];

export function getFriendlyComparableSampleGroupName(type: SampleTypeGroup): string {
  switch (type) {
    case ComparableSampleGroupType.COMMUNAL_WASTEWATER:
      return 'Communal wastewater';
    case ComparableSampleGroupType.CLINICAL_WASTEWATER:
      return 'Clinical wastewater';
    case ComparableSampleGroupType.RAW_WASTEWATER:
      return 'Raw wastewater';
    case ComparableSampleGroupType.TREATED_WASTEWATER:
      return 'Treated wastewater';
    case ComparableSampleGroupType.WATER_TREATMENT:
      return 'Water treatment';
    case AllProjectSampleTypesGroup.ALL_PROJECT_SAMPLING_POINTS:
      return 'All samples';
    default:
      return friendlySampleType(type);
  }
}

export const orderedComparableSampleGroupTypesForMetric = {
  [MetricMode.ARGI]: [
    ComparableSampleGroupType.COMMUNAL_WASTEWATER,
    ComparableSampleGroupType.CLINICAL_WASTEWATER,
    ComparableSampleGroupType.RAW_WASTEWATER,
    ComparableSampleGroupType.TREATED_WASTEWATER,
  ],
  [MetricMode.RISK]: [
    ComparableSampleGroupType.TREATED_WASTEWATER,
    ComparableSampleGroupType.RAW_WASTEWATER,
    ComparableSampleGroupType.CLINICAL_WASTEWATER,
    ComparableSampleGroupType.COMMUNAL_WASTEWATER,
  ],
  [MetricMode.REDUCTION]: [ComparableSampleGroupType.WATER_TREATMENT],
};

export function getOrderedSampleGroupTypesForMetric(metricMode: MetricMode | undefined): SampleTypeGroup[] {
  switch (metricMode) {
    case MetricMode.ARGI:
      return uniq([...orderedComparableSampleGroupTypesForMetric[MetricMode.ARGI], ...allSampleGroupTypes]);
    case MetricMode.RISK:
      return uniq([...orderedComparableSampleGroupTypesForMetric[MetricMode.RISK], ...allSampleGroupTypes]);
    case MetricMode.REDUCTION:
      return uniq([...orderedComparableSampleGroupTypesForMetric[MetricMode.REDUCTION], ...allSampleGroupTypes]);
    default:
      return allSampleGroupTypes;
  }
}

// NOTE: Group specifications are ordered from specific to more general
//       Selection of available (and interesting) groups for a project relies on this
//       by only keeping only the first of each set of groupings that select identical set of sampling points
export const comparableGroupSpecs: Record<SampleTypeGroup, ComparableSampleGroupSpec> = {
  [ComparableSampleGroupType.COMMUNAL_WASTEWATER]: {
    type: ComparableSampleGroupType.COMMUNAL_WASTEWATER,
    processMode: ProcessMode.BEFORE,
    smplPntFilterer: smplPnt =>
      smplPnt.type === SampleType.WASTEWATER &&
      !!smplPnt.subtype &&
      [
        SampleSubType.URBAN_WASTEWATER_RAW,
        SampleSubType.URBAN_RUNOFF,
        SampleSubType.SEPTIC_TANK_RAW,
        SampleSubType.BLACK_WATER,
      ].includes(smplPnt.subtype),
  },
  [ComparableSampleGroupType.CLINICAL_WASTEWATER]: {
    type: ComparableSampleGroupType.CLINICAL_WASTEWATER,
    processMode: ProcessMode.BEFORE,
    smplPntFilterer: smplPnt =>
      smplPnt.type === SampleType.WASTEWATER &&
      !!smplPnt.subtype &&
      [SampleSubType.HOSPITAL_WASTEWATER_RAW, SampleSubType.PRIMARY_HEALTH_CARE_RAW].includes(smplPnt.subtype),
  },
  [ComparableSampleGroupType.RAW_WASTEWATER]: {
    type: ComparableSampleGroupType.RAW_WASTEWATER,
    processMode: ProcessMode.BEFORE,
    smplPntFilterer: smplPnt =>
      smplPnt.type === SampleType.WASTEWATER && !!smplPnt.subtype?.endsWith(SmplPntSubTypePostFix._RAW),
  },
  [ComparableSampleGroupType.TREATED_WASTEWATER]: {
    type: ComparableSampleGroupType.TREATED_WASTEWATER,
    processMode: ProcessMode.AFTER,
    smplPntFilterer: smplPnt =>
      smplPnt.type === SampleType.WASTEWATER && !!smplPnt.subtype?.endsWith(SmplPntSubTypePostFix._TREATED),
  },
  [ComparableSampleGroupType.WATER_TREATMENT]: {
    type: ComparableSampleGroupType.WATER_TREATMENT,
    processMode: ProcessMode.DURING,
    smplPntFilterer: smplPnt =>
      smplPnt.type === SampleType.WASTEWATER &&
      values(SmplPntSubTypePostFix).some(suffix => smplPnt.subtype?.endsWith(suffix)),
  },
  ...(Object.fromEntries(
    values(SampleType).map(type => [
      type,
      {
        type,
        smplPntFilterer: (smplPnt: SamplingPoint) => smplPnt.type === type,
      },
    ]),
  ) as Record<SampleType, ComparableSampleGroupSpec>),
  [AllProjectSampleTypesGroup.ALL_PROJECT_SAMPLING_POINTS]: {
    type: AllProjectSampleTypesGroup.ALL_PROJECT_SAMPLING_POINTS,
    processMode: undefined,
    smplPntFilterer: () => true,
  },
};

export function getDefaulProcessModeForMetricAndSelectedSamplingPoints(
  selectedGroupOrDefaultAll: SampleTypeGroup | undefined,
  metricMode: MetricMode,
  samples: FullSample[],
): ProcessMode {
  if (!selectedGroupOrDefaultAll) {
    return ProcessMode.BEFORE; // Loading time default
  }
  const atLeastOneSampleHasAfterAbundances = samples.some(sample =>
    afterSubtypePostfixes.some(postfix => sample.samplingPoint.subtype?.endsWith(postfix)),
  );
  const processMode =
    comparableGroupSpecs[selectedGroupOrDefaultAll].processMode ??
    (metricMode === MetricMode.REDUCTION
      ? ProcessMode.DURING
      : metricMode === MetricMode.ARGI
        ? ProcessMode.BEFORE
        : // Here metricMode === MetricMode.RISK
          atLeastOneSampleHasAfterAbundances &&
            selectedGroupOrDefaultAll !== AllProjectSampleTypesGroup.ALL_PROJECT_SAMPLING_POINTS // Disables implicit switching to AFTER mode for Risk overview
          ? ProcessMode.AFTER
          : ProcessMode.BEFORE);
  return processMode;
}

export function sampleBelongsToSampleGroup(sample: Sample, group: SampleTypeGroup): boolean {
  return comparableGroupSpecs[group].smplPntFilterer(sample.samplingPoint);
}

export function filterGroupSamplingPoints<T extends SamplingPoint>(
  samplingPoints: SamplingPoint[],
  group: SampleTypeGroup,
): T[] {
  return samplingPoints.filter(comparableGroupSpecs[group].smplPntFilterer) as T[];
}

export function getComparableSamplingPointGroups(
  samplingPoints: SamplingPoint[],
  metricMode: MetricMode | undefined,
  _keepDuplicateAll: boolean = false,
): SmplPntGroup[] {
  const orderedSmplPntGroupTypes = getOrderedSampleGroupTypesForMetric(metricMode);
  const comparableGroupsWithUniqSmplPntCombinations = chain(orderedSmplPntGroupTypes)
    .map(type => comparableGroupSpecs[type])
    .map(spec => ({ type: spec.type, smplPnts: samplingPoints.filter(spec.smplPntFilterer) }))
    .filter(group => group.smplPnts.length > 0)
    .value();
  return comparableGroupsWithUniqSmplPntCombinations;
}
